import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { AuthError, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { resetConsumerSubscriptionReadyCache } from "@/lib/consumerEntitlements";
import { AUTH_ERROR_MESSAGES, translateAuthError } from "@/lib/authErrors";
import {
  emailLocalPart,
  humanizeEmailPrefix,
  resolveDisplayName,
} from "@/lib/profileDisplay";
import { readAvatarUrlFromUser } from "@/lib/profileAvatar";
import { getLaunchMode } from "@/lib/launchMode";

const GUEST_KEY = "pulse.guest";

export type OAuthProviderType = "apple" | "google" | "email" | "guest";

export interface AuthUser {
  id?: string;
  provider: OAuthProviderType;
  name: string;
  email?: string;
  avatarUrl?: string | null;
  loggedInAt: number;
  isGuest: boolean;
}

export type SignInResult =
  | { ok: true }
  | { ok: false; invalidCredentials: true }
  | { ok: false; invalidCredentials: false; message: string };

interface AuthContextValue {
  user: AuthUser | null;
  hydrated: boolean;
  authError: string | null;
  clearAuthError: () => void;
  signInWithGoogle: () => Promise<string | null>;
  signInWithApple: () => Promise<string | null>;
  signInWithPassword: (email: string, password: string) => Promise<SignInResult>;
  signUpWithPassword: (email: string, password: string) => Promise<boolean>;
  resetPasswordForEmail: (email: string) => Promise<string | null>;
  refreshSession: () => Promise<void>;
  skip: () => void;
  signOut: () => Promise<string | null>;
  isGuest: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readGuest(): boolean {
  try {
    return localStorage.getItem(GUEST_KEY) === "1";
  } catch {
    return false;
  }
}

function writeGuest(on: boolean) {
  try {
    if (on) localStorage.setItem(GUEST_KEY, "1");
    else localStorage.removeItem(GUEST_KEY);
  } catch {}
}

function providerFromUser(user: User): OAuthProviderType {
  const p = user.app_metadata?.provider;
  if (p === "google") return "google";
  if (p === "apple") return "apple";
  if (user.email) return "email";
  return "email";
}

function mapAuthUser(u: User): AuthUser {
  const meta = u.user_metadata ?? {};
  const name = resolveDisplayName({
    full_name: meta.full_name as string | undefined,
    name: meta.name as string | undefined,
    email: u.email,
  });
  return {
    id: u.id,
    provider: providerFromUser(u),
    name,
    email: u.email,
    avatarUrl: readAvatarUrlFromUser(u),
    loggedInAt: new Date(u.last_sign_in_at ?? Date.now()).getTime(),
    isGuest: false,
  };
}

/** Prefer server-validated user (fresh raw_user_meta_data) over cached session JWT. */
async function resolveAuthUser(session: Session | null): Promise<AuthUser | null> {
  if (!session?.user) return null;

  const {
    data: { user: freshUser },
    error,
  } = await supabase.auth.getUser();

  if (!error && freshUser) {
    return mapAuthUser(freshUser);
  }

  return mapAuthUser(session.user);
}

function guestUser(): AuthUser {
  return {
    provider: "guest",
    name: "Guest",
    loggedInAt: Date.now(),
    isGuest: true,
  };
}

function isInvalidCredentials(error: AuthError): boolean {
  const msg = error.message.toLowerCase();
  return (
    error.code === "invalid_credentials" ||
    msg.includes("invalid login credentials") ||
    msg.includes("invalid credentials")
  );
}

export function AuthProvider({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient?: QueryClient;
}) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearQueryCache = useCallback(() => {
    resetConsumerSubscriptionReadyCache();
    queryClient?.clear();
  }, [queryClient]);

  /** Sync UI from local session JWT / guest flag — no network. */
  const applySessionLocal = useCallback((session: Session | null) => {
    if (session?.user) {
      writeGuest(false);
      setUser(mapAuthUser(session.user));
      return;
    }
    if (readGuest()) {
      setUser(guestUser());
      return;
    }
    writeGuest(false);
    setUser(null);
  }, []);

  /** Background refresh of raw_user_meta_data via getUser(); does not gate hydrated. */
  const refreshUserFromServer = useCallback(async (session: Session | null) => {
    if (!session?.user) return;
    const resolved = await resolveAuthUser(session);
    setUser(resolved);
  }, []);

  const applySession = useCallback(async (session: Session | null) => {
    applySessionLocal(session);
    await refreshUserFromServer(session);
  }, [applySessionLocal, refreshUserFromServer]);

  useEffect(() => {
    let mounted = true;

    // Hydrate from local getSession() immediately so splash isn't blocked on getUser() RTT.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      applySessionLocal(session);
      setHydrated(true);
      void refreshUserFromServer(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        clearQueryCache();
      }
      applySessionLocal(session);
      setHydrated(true);
      void refreshUserFromServer(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applySessionLocal, refreshUserFromServer, clearQueryCache]);

  const handleAuthError = (error: AuthError | null) => {
    if (error) {
      const message = translateAuthError(error);
      setAuthError(message);
      return message;
    }
    setAuthError(null);
    return null;
  };

  const signInWithGoogle = async () => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    return handleAuthError(error);
  };

  const signInWithApple = async () => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: window.location.origin },
    });
    return handleAuthError(error);
  };

  const signInWithPassword = async (email: string, password: string): Promise<SignInResult> => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) return { ok: true };
    if (isInvalidCredentials(error)) {
      setAuthError(AUTH_ERROR_MESSAGES.invalidCredentials);
      return { ok: false, invalidCredentials: true };
    }
    const message = translateAuthError(error);
    setAuthError(message);
    return { ok: false, invalidCredentials: false, message };
  };

  const signUpWithPassword = async (email: string, password: string): Promise<boolean> => {
    setAuthError(null);
    if (getLaunchMode() !== "live") {
      setAuthError("Self-registration is closed during beta. Use an invite link.");
      return false;
    }
    const localPart = emailLocalPart(email);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: localPart
        ? {
            data: {
              full_name: humanizeEmailPrefix(localPart),
              name: localPart,
            },
          }
        : undefined,
    });
    if (error) {
      handleAuthError(error);
      return false;
    }
    if (data.session) return true;

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      handleAuthError(signInError);
      return false;
    }
    return true;
  };

  const resetPasswordForEmail = async (email: string): Promise<string | null> => {
    setAuthError(null);
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) {
      return handleAuthError(error);
    }
    return null;
  };

  const refreshSession = async () => {
    const {
      data: { user: freshUser },
      error,
    } = await supabase.auth.getUser();

    if (!error && freshUser) {
      writeGuest(false);
      setUser(mapAuthUser(freshUser));
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();
    await applySession(session);
  };

  const skip = () => {
    setAuthError(null);
    writeGuest(true);
    setUser(guestUser());
  };

  const signOut = async (): Promise<string | null> => {
    setAuthError(null);

    if (user?.isGuest || readGuest()) {
      writeGuest(false);
      setUser(null);
      clearQueryCache();
      return null;
    }

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        const message = translateAuthError(error);
        setAuthError(message);
        return message;
      }
      writeGuest(false);
      setUser(null);
      clearQueryCache();
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : AUTH_ERROR_MESSAGES.signOutFailed;
      setAuthError(message);
      return message;
    }
  };

  const value: AuthContextValue = {
    user,
    hydrated,
    authError,
    clearAuthError: () => setAuthError(null),
    signInWithGoogle,
    signInWithApple,
    signInWithPassword,
    signUpWithPassword,
    resetPasswordForEmail,
    refreshSession,
    skip,
    signOut,
    isGuest: user?.isGuest ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
